import {useRef} from 'react';
import {
    Alert,
    SimpleGrid,
    Text,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

import {useDefectSignatures} from '../hooks/useDefectSignatures.ts';
import {useDefectSignaturesSearch} from '../hooks/useDefectSignaturesSearch.ts';
import {useDefectSignaturesTableColumns} from '../hooks/useDefectSignaturesTableColumns.tsx';
import {DefectSummaryStrip} from '../components/DefectSummaryStrip.tsx';
import {useDefectSignaturesSelection} from '../hooks/useDefectSignaturesSelection.ts';
import {DefectSignature} from '../types';
import config from '../config';

export const RecordsTable = () => {

    const {columns} = useDefectSignaturesTableColumns();
    const navigate = useNavigate();

    const {
        records,
        // The keyword-filtered set BEFORE sorting — i.e. the same rows as
        // `records`. Kept distinct in the footer only because the unfiltered
        // window size is what makes "3 of 20 loaded" an honest claim.
        items,
        summary,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        nextToken,
        fetchRecords,
    } = useDefectSignatures();

    const {
        selection,
        handleRowSelection,
        handleClearSelection,
    } = useDefectSignaturesSelection();

    // The rows the bulk-delete button acts on. Gated on RBAC so a user without
    // the permission gets no checkboxes at all, rather than checkboxes whose
    // action then fails.
    const selectedRecords = records.filter(
        (record: DefectSignature) => selection[record.id] === true
    );

    // Same module-scoped store the list hook filters on (FNP-815): this component
    // owns the input, useDefectSignatures owns the filtering, and they must share
    // one keyword. Never swap this for a local useState.
    const {
        keyword,
        handleFilterByKeyword,
    } = useDefectSignaturesSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <DefectSummaryStrip summary={summary}/>

            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load defects'>
                    The testing API did not return defect signatures. Try Reload; if it persists
                    the API may be unavailable in this environment.
                </Alert>
            )}

            {/*
              The empty state distinguishes "nothing found" from "nothing wrong".
              An empty defects table is GOOD NEWS and must not read like a broken
              page — but an empty table caused by a keyword or filter is a
              different message entirely, and conflating them sends an operator
              hunting for a bug in the console.

              ⚠️ WHY NOT `noRecordsText` (checklist §3) — FNP-923.
              That prop is a SINGLE STRING and cannot express two different
              situations. Using it would force one message onto both, which is
              precisely the confusion above. This Alert is a deliberate
              over-delivery on that box, not a gap; the checklist has been
              updated to ask for the distinction rather than the prop.
            */}
            {!loading && !isError && records.length === 0 && (
                <Alert color={keyword.trim() ? 'gray' : 'green'} title={keyword.trim() ? 'No matching defects' : 'No defects recorded'}>
                    {keyword.trim()
                        ? `Nothing matches "${keyword.trim()}". Clear the search or the filter to see all defects.`
                        : 'The fleet has not recorded any defect signatures yet. Either nothing has broken, or no synthetic user has woken since the engine was deployed.'}
                </Alert>
            )}

            {/*
              ROW SELECTION EXISTS ONLY FOR DELETE (FNP-969).

              FNP-923 recorded that this table had no batch operation to justify a
              checkbox column, and listed "DELETION does not exist — signatures are
              derived state" as one of the reasons. That third bullet is now WRONG:
              `deleteDefectSignature` was added to the API and verified live, so
              clearing an environment is a real bulk operation. The first two
              reasons still stand and still mean no other batch action belongs here:

                · FILING is per-defect by construction — the drawer generates a
                  ticket body from THIS signature's evidence.
                · RESOLUTION is the sweep's conclusion from an absence of
                  occurrences, never a human bulk action. `resolveDefectSignature`
                  is @aws_iam-only precisely so a browser cannot declare defects
                  fixed.

              ⚠️ Delete is therefore NOT a general-purpose "act on these rows"
              affordance — it is specifically for clearing an environment, and the
              confirmation modal says so. Do not hang other batch actions off this
              selection.
            */}
            <MantineDataTable<DefectSignature>
                columns={columns}
                records={records}
                fetching={loading}
                idAccessor={'id'}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    (items as DefectSignature[]).forEach(item => {
                        handleRowSelection(item.id, true);
                    });
                }}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) =>
                    navigate(`${config.basePath}/${encodeURIComponent(record.id)}`)}
            />

            <DataTableTokenPagination
                // The LOADED count, not a total — the API returns no total and
                // fabricating "of N" would be a number the page cannot know.
                // `records` is already keyword-filtered, so it carries the
                // MATCHED count; `visibleItems.length` is the window it was
                // matched against.
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'defect'}
                nextToken={nextToken}
                handleLoadMore={fetchRecords}
            />

            <Text size={'xs'} c={'dimmed'}>
                One row per defect signature, not per observation. Sorting orders the loaded
                rows.
            </Text>
        </SimpleGrid>
    );
};
